import { useState, useEffect } from "react";
import { useAuth } from "../../../context/contextAuth.js";
import {
  getConfigAssets,
  getConfigScanProgress,
} from "../../../api/get/azure/get-config.js";
import { scanConfig } from "../../../api/post/post-azure.js";

const useAzureConfig = () => {
  const { user, userId } = useAuth();
  const [index, setIndex] = useState(0);  
  const [clientId, setClientId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const [configLoading, setConfigLoading] = useState(false);
  const [configScanLoading, setConfigScanLoading] = useState(false);
  const [configScanProgress, setConfigScanProgress] = useState(null);
  const [configView, setConfigView] = useState(false);

  const [configAssets, setConfigAssets] = useState(null);
  const [cAzureConfigAll, setCAzureConfigAll] = useState(null);
  const [saveScore, setSaveScore] = useState(0);
  const [error, setError] = useState({
    message: null,
    card: false
  });

  const [dashboard, setDashboard] = useState(null);
  const [service, setService] = useState(null);
  const [resourceGroup, setResourceGroup] = useState(null); // Changed from region to resourceGroup for Azure context

  useEffect(() => {
    if (!user || !userId) return;
    (async () => {
      try {
        setConfigLoading(true);
        await fetchAzureConfigAssets(userId);
      } catch (e) {
        console.error("Error in initData:", e);
      } finally {
        setConfigLoading(false);
      }
    })();
  }, [user, userId]);

  const initData = async () => {
    if (!user || !userId) return;
    return fetchAzureConfigAssets(userId);
  };

  const fetchAzureConfigAssets = async (userId) => {
    try {
      const assets = await getConfigAssets(userId);

      setConfigAssets(assets || []);
      setCAzureConfigAll(assets?.[0]?.misconfig || null);
      setSaveScore(assets?.[0]?.ss ?? 0);

      return assets;
    } catch (e) {
      console.error("Error loading Azure config assets:", e);
      setConfigAssets([]);
      setCAzureConfigAll(null);
      setSaveScore(0);
      throw e;
    }
  };

  const refreshLatestAssessment = async (expectedScanId) => {
    let latestAssets = [];
    for (let attempt = 0; attempt < 5; attempt += 1) {
      latestAssets = await fetchAzureConfigAssets(userId);
      if (!expectedScanId || latestAssets?.[0]?.azure_uuid === expectedScanId) {
        return latestAssets;
      }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    throw new Error("The Azure assessment completed, but its newest result is not available yet");
  };

  const handleConfigScan = async (e) => {
    if (e) e.preventDefault();
    setConfigScanLoading(true);
    setError({ message: null, card: false });
    setConfigScanProgress({
      status: "running",
      stage: "starting",
      progress: 1,
      message: "Starting Azure assessment",
    });
    const progressTimer = window.setInterval(async () => {
      try {
        const progress = await getConfigScanProgress(userId);
        if (progress) setConfigScanProgress(progress);
      } catch (progressError) {
        console.warn("Unable to refresh Azure scan progress:", progressError);
      }
    }, 1000);

    try {
      const result = await scanConfig(userId, {
        clientId, 
        tenantId, 
        clientSecret 
      });
      const finalProgress = await getConfigScanProgress(userId);
      if (finalProgress) setConfigScanProgress(finalProgress);
      await refreshLatestAssessment(result?.data?.scanId);
      setConfigView(true);
    } catch (err) {
      setConfigView(false);
      setError({
        message: err.message || "Azure Scan failed. Please check credentials.",
        card: true
      });
    } finally {
      window.clearInterval(progressTimer);
      setConfigScanLoading(false);
      setClientSecret("");
      setTimeout(() => setError({ message: null, card: false }), 6000);
    }
  };

  const resetState = () => {
    setIndex(0);
    setClientId("");
    setTenantId("");
    setClientSecret("");
    setConfigLoading(false);
    setConfigScanLoading(false);
    setConfigScanProgress(null);
    setConfigView(false);
    setConfigAssets([]);
    setCAzureConfigAll(null);
    setSaveScore(0);

    setError({ message: null, card: false });
    setDashboard(false);
    setService(false);
    setResourceGroup(false);
  };

  return {
    index,
    setIndex,
    clientId,
    setClientId,
    tenantId,
    setTenantId,
    clientSecret,
    setClientSecret,
    
    configLoading,
    setConfigLoading,
    configScanLoading,
    setConfigScanLoading,
    configScanProgress,
    setConfigScanProgress,
    configView,
    setConfigView,

    configAssets,
    setConfigAssets,
    cAzureConfigAll,
    setCAzureConfigAll,

    error,
    setError,
    handleConfigScan,
    fetchAzureConfigAssets,
    initData,
    resetState,

    dashboard,
    setDashboard,
    service,
    setService,
    resourceGroup,
    setResourceGroup,
    saveScore,
    setSaveScore,
  };
};

export default useAzureConfig;
